// Cloud Bot — backend-resident Multiplier Hunter that keeps trading even
// when the user closes their phone or browser. Calls the FastAPI endpoints
// at /api/bot/* and polls for live status every 3s while running.
//
// Flow:
//   1. User picks an account + risk %, taps "Start in Cloud"
//   2. Frontend POSTs /api/bot/start with the Deriv access_token
//   3. Backend opens its own WS, runs the strategy loop
//   4. Frontend polls /api/bot/status to render live stats + log
//   5. User can close the tab. Bot keeps running on the server.
//   6. User reopens app → same panel resumes showing live state.
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2, Square, Cloud, AlertTriangle, RefreshCw, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { api } from "@/lib/api";
import { BrrForcedPicker } from "./BrrForcedPicker";
import { RiskProfileSelector } from "./RiskProfileSelector";
import { ColdPairsBadge } from "./ColdPairsBadge";
import { TrailConfigPanel } from "./TrailConfigPanel";
import { MarketConditionBadge } from "./MarketConditionBadge";
import { BrrGateTraceWidget } from "./BrrGateTraceWidget";
import RecentRejectionsWidget from "./RecentRejectionsWidget";

// MasterSpec §C — tier display metadata (mirrors backend TIER_CONFIG).
const TIER_BADGE = {
  1: { name: "STARTER",  color: "bg-vts-silver/20 text-vts-silver border-vts-line/60" },
  2: { name: "BASIC",    color: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  3: { name: "STANDARD", color: "bg-vts-blue/20 text-vts-blue border-vts-blue/50" },
  4: { name: "ADVANCED", color: "bg-amber-500/20 text-amber-300 border-amber-500/50" },
  5: { name: "PRO",      color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/60" },
};
const TIER_NEXT_DEPOSIT = { 1: 100, 2: 200, 3: 500, 4: 1000, 5: null };

// VTS_DIFFERS_StakePayoutFix.pdf — T1 base raised $0.65 → $1.00 so the
// proposal payout clears Deriv's 7% gate (sub-$1 stakes hit the flat-minimum
// payout and fail). Single source of truth lives on the backend; if you
// change this table also update bot_worker.TIER_CONFIG.
const TIER_PREVIEW = [
  { id: 1, min: 50,    max: 99,     name: "STARTER",  base: 1.00, wins: 2 },
  { id: 2, min: 100,   max: 199,    name: "BASIC",    base: 1.00, wins: 3 },
  { id: 3, min: 200,   max: 499,    name: "STANDARD", base: 2.00, wins: 4 },
  { id: 4, min: 500,   max: 999,    name: "ADVANCED", base: 5.00, wins: 4 },
  { id: 5, min: 1000,  max: 999999, name: "PRO",      base: 10.00, wins: 4 },
];

const tierFromBalance = (bal) => {
  if (bal == null || bal < 50) return null;
  return TIER_PREVIEW.find((t) => bal >= t.min && bal <= t.max) || TIER_PREVIEW[4];
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const levelColor = (lv) => {
  if (lv === "error") return "text-vts-loss";
  if (lv === "skip") return "text-vts-silver/60";
  if (lv === "buy") return "text-vts-profit";
  if (lv === "info") return "text-vts-blue";
  return "text-vts-silver/80";
};

// Human-readable labels for each underlying strategy that can fire trades.
// Used in the Recent Trades badge + Per-Strategy leaderboard so the user can
// instantly see WHICH signal source produced each trade when running in
// "all" (parallel) mode.
const STRATEGY_LABELS = {
  multiplier: "Multiplier",
  rise_fall: "R/F",
  rise_fall_1m: "R/F 1m",
  rise_fall_5m: "R/F 5m",
  rise_fall_15m: "R/F 15m",
  bollinger: "BB Bounce",
  digit_over: "DIGITOVER (off)",
  digit_under: "DIGITUNDER (off)",
  digit_ou: "Digit O/U (off)",
  digit_eo: "Even/Odd (off)",
  digit_diff: "DIGITDIFF (off)",
  digit_notouch: "NOTOUCH",
  digit: "Digit",
  accu: "Accumulator",
  both: "Both",
  all: "All",
};

const STRATEGY_COLORS = {
  multiplier: "bg-vts-blue/15 text-vts-blue border-vts-blue/40",
  rise_fall: "bg-vts-profit/15 text-vts-profit border-vts-profit/40",
  rise_fall_1m: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40",
  rise_fall_5m: "bg-vts-profit/15 text-vts-profit border-vts-profit/40",
  rise_fall_15m: "bg-emerald-600/20 text-emerald-300 border-emerald-500/50",
  bollinger: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  digit_over: "bg-vts-silver/10 text-vts-silver/50 border-vts-line/40 line-through",
  digit_under: "bg-vts-silver/10 text-vts-silver/50 border-vts-line/40 line-through",
  digit_ou: "bg-vts-silver/10 text-vts-silver/50 border-vts-line/40 line-through",
  digit_eo: "bg-pink-500/15 text-pink-300 border-pink-500/40",
  digit_diff: "bg-vts-silver/10 text-vts-silver/50 border-vts-line/40 line-through",
  digit_notouch: "bg-orange-500/15 text-orange-300 border-orange-500/40",
  digit: "bg-purple-500/15 text-purple-300 border-purple-500/40",
  accu: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  both: "bg-vts-blue/15 text-vts-blue border-vts-blue/40",
  all: "bg-vts-silver/15 text-vts-silver border-vts-silver/40",
};

const CloudBotPanel = ({ balance, currency, operatorMode = false }) => {
  // operatorMode=false (default) → CLEAN user view: hide all technical
  //   panels (Risk Profile, Cold Pairs, BRR Forced, Trail Config,
  //   BRR Gate Trace, Market Condition, Filter Stats, Block Reasons).
  // operatorMode=true → mounted from /operator with admin JWT — shows
  //   every diagnostic + tuning panel.
  const { session } = useAuth();
  const accountId = session?.account_id;
  const accessToken = session?.token;
  const accounts = session?.accounts || [];
  const isDemo = accountId && (accountId.startsWith("VRTC") || accountId.startsWith("DOT"));

  const [selectedAccount, setSelectedAccount] = useState(accountId || "");
  const [strategy, setStrategy] = useState("all"); // Feb 2026 — default to "all" (parallel) per user request for production launch. EMA + Multiplier + R/F + ACCU + NoTouch + EO fire concurrently on every qualifying signal. Auto-disable culls underperformers per-strategy.
  const [rfDuration, setRfDuration] = useState(5);
  const [stake, setStake] = useState(5);
  // Feb 2026 — default flipped from "percent" → "fixed" after user
  // confusion: typing a stake value did nothing because percent mode
  // silently used risk_pct instead. Fixed mode = WYSIWYG ($5 typed → $5
  // fires). Percent mode still available via the toggle for advanced users.
  // Feb 2026 v7.6 — default to "percent" of balance per user request:
  // "let this select by default % of balance". Auto-scales stake as
  // balance grows/shrinks, safer than fixed amount for tier transitions.
  const [riskMode, setRiskMode] = useState("percent");
  const [riskPct, setRiskPct] = useState(2);  // Hybrid: 2% growth velocity, hard caps protect downside
  // Optional safety caps. 0/empty = unlimited / disabled. Concurrent cap
  // defaults to 6 — keeps the bot from spamming entries across all 28 pairs.
  const [maxTrades, setMaxTrades] = useState("");
  const [maxConcurrent, setMaxConcurrent] = useState("6");
  const [dailySL, setDailySL] = useState("");
  const [dailyTP, setDailyTP] = useState("");
  // Entry-mode: "closed" (default, M1 candle close confirmation, fewer/better
  // trades) vs "forming" (live tick on in-progress candle, more/noisier).
  const [entryMode, setEntryMode] = useState("closed");
  // RSI(14) confirmation filter — skips crosses where RSI disagrees with
  // the cross direction. Feb 2026 — defaulted ON per user request for higher
  // accuracy. Pairs with the score≥3 EMA gate so every fire has multiple
  // independent confirmations.
  const [rsiConfirm, setRsiConfirm] = useState(true);
  // M5 higher-timeframe trend alignment — only fire M1 crosses that match
  // the M5 EMA(20) direction. Filters bad TRADES across ALL pairs without
  // blocking entire symbols. Feb 2026 — defaulted ON per user request.
  const [htfTrend, setHtfTrend] = useState(true);
  const [autoBlacklist, setAutoBlacklist] = useState(true);  // 13-Changes #09: ON by default
  // Auto-pause any strategy whose run-time win-rate falls below 40% after 10
  // closed trades. Only kicks in when running 'both' or 'all'. The other
  // strategies keep firing — far better UX than fully stopping the bot.
  const [autoDisableLosing, setAutoDisableLosing] = useState(true);
  // Feb 2026 — user-tunable ACCU Take-Profit % dial.
  // 5% / 10% / 15% / 20%. Smaller TP = higher hit rate (>91% breakeven),
  // larger TP = bigger wins but lower hit rate. Backend clamps 3-40% and
  // auto-scales the trail arm / lock distance to match.
  const [accuTpPct, setAccuTpPct] = useState(20);
  const [status, setStatus] = useState(null);
  const [trades, setTrades] = useState([]);
  const [symbolStats, setSymbolStats] = useState([]);
  const [strategyStats, setStrategyStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // MasterSpec §B — Risk Disclaimer modal (shown on every Start).
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);
  const [disclaimerChecked, setDisclaimerChecked] = useState(false);

  useEffect(() => {
    if (accountId) setSelectedAccount(accountId);
  }, [accountId]);

  // Poll status every 3s. Slower when idle (12s) to save battery + API calls.
  const fetchStatus = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const res = await api.get(`/bot/status/${encodeURIComponent(selectedAccount)}`);
      setStatus(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load status");
    }
  }, [selectedAccount]);

  // Pull recent trades — works even when older entries get pushed out of the
  // rolling activity log. Persists across bot restarts because they're stored
  // in MongoDB.
  const fetchTrades = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const res = await api.get(`/bot/trades/${encodeURIComponent(selectedAccount)}?limit=25`);
      setTrades(res.data?.trades || []);
    } catch {
      // silent — trades are non-critical compared to bot status
    }
  }, [selectedAccount]);

  // Per-symbol win-rate / P&L aggregation. Polled less frequently than the
  // live status — the leaderboard only shifts when contracts close.
  const fetchSymbolStats = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const res = await api.get(`/bot/symbol-stats/${encodeURIComponent(selectedAccount)}`);
      setSymbolStats(res.data?.symbols || []);
    } catch {
      // silent
    }
  }, [selectedAccount]);

  // Per-strategy win-rate / P&L — critical in 'all' (parallel) mode so the
  // user can see WHICH strategy is actually profitable vs. dragging P&L down.
  const fetchStrategyStats = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const res = await api.get(`/bot/strategy-stats/${encodeURIComponent(selectedAccount)}`);
      setStrategyStats(res.data?.strategies || []);
    } catch {
      // silent
    }
  }, [selectedAccount]);

  // Duration Performance — live WR per (symbol, duration) bucket so the user
  // can see whether 5m vs 15m R/F is actually winning on THEIR account.
  // Aggregated across symbols into one row per duration tag.
  const [durationStats, setDurationStats] = useState([]);
  const fetchDurationStats = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const res = await api.get(`/track/duration-stats`, {
        params: { account_id: selectedAccount, days: 30 },
      });
      const bySymbol = res.data?.by_symbol || {};
      // Flatten {symbol: [{duration, won, lost, total, win_rate, profit}]}
      // into per-duration rolled-up totals.
      const rollup = {};
      Object.values(bySymbol).forEach((rows) => {
        rows.forEach((r) => {
          const key = r.duration;
          if (!rollup[key]) {
            rollup[key] = { duration: key, won: 0, lost: 0, total: 0, profit: 0 };
          }
          rollup[key].won += r.won || 0;
          rollup[key].lost += r.lost || 0;
          rollup[key].total += r.total || 0;
          rollup[key].profit += r.profit || 0;
        });
      });
      const arr = Object.values(rollup)
        .map((r) => ({
          ...r,
          win_rate: r.total ? r.won / r.total : 0,
        }))
        .sort((a, b) => b.total - a.total);
      setDurationStats(arr);
    } catch {
      // silent — endpoint exists but returns empty for cold accounts
    }
  }, [selectedAccount]);

  useEffect(() => {
    fetchStatus();
    fetchTrades();
    fetchSymbolStats();
    fetchStrategyStats();
    fetchDurationStats();
    const running = status?.is_running;
    const interval = setInterval(() => {
      fetchStatus();
      fetchTrades();
      fetchSymbolStats();
      fetchStrategyStats();
      fetchDurationStats();
    }, running ? 3000 : 12000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccount, status?.is_running]);

  // Toast whenever a NEW strategy gets auto-paused this run (so users know
  // why one of their parallel scanners has gone quiet).
  const lastDisabledRef = useRef([]);
  useEffect(() => {
    const current = status?.disabled_strategies || [];
    const previous = lastDisabledRef.current;
    const newly = current.filter((s) => !previous.includes(s));
    if (newly.length > 0 && previous.length > 0) {
      // Skip the very first transition (initial mount can flicker stale state)
      for (const s of newly) {
        const label = STRATEGY_LABELS[s] || s;
        toast.warning(`Auto-paused "${label}"`, {
          description: "Win-rate dropped below 40% over 10+ trades — other strategies keep running.",
        });
      }
    }
    lastDisabledRef.current = current;
  }, [status?.disabled_strategies]);

  // When the bot transitions from running → stopped with a reason, surface
  // it as a toast so the user knows why (otherwise it can look like the bot
  // just silently quit).
  // For the SPECIFIC "Server restarted" stop reason (which happens on every
  // backend deploy / hot-reload), auto-resume the bot using the same config
  // the user originally started with. Avoids the "Bot keeps stopping"
  // frustration during dev iterations and live deploys.
  const lastReasonRef = useMemo(() => ({ current: null }), []);
  const autoResumeAttemptedRef = useMemo(() => ({ current: null }), []);
  useEffect(() => {
    const reason = status?.stop_reason;
    if (reason && reason !== lastReasonRef.current && !status?.is_running) {
      lastReasonRef.current = reason;
      const lowered = reason.toLowerCase();
      const isServerRestart = lowered.includes("server restarted");
      const isSessionExpired =
        lowered.includes("session expired") ||
        lowered.includes("log in again") ||
        lowered.includes("invalid token") ||
        lowered.includes("unauthor");
      if (isSessionExpired) {
        // Don't auto-resume — the access token is dead. Tell the user
        // exactly what to do.
        toast.error("Deriv session expired", {
          description: "Tap Login with Deriv at the top to re-auth, then start the bot again.",
          duration: 12000,
        });
      } else if (isServerRestart && accessToken && selectedAccount &&
          autoResumeAttemptedRef.current !== selectedAccount) {
        autoResumeAttemptedRef.current = selectedAccount;
        toast("Auto-resuming bot after server update…", { description: reason });
        // Re-issue start with the user's current panel config. Throttled to
        // one attempt per account selection so we never thrash.
        (async () => {
          try {
            const cfg = {
              strategy,
              rf_duration: Math.max(1, parseInt(rfDuration, 10) || 5),
              rf_duration_unit: "m",
              risk_mode: riskMode,
              risk_pct: parseFloat(riskPct) || 2,
              stake: Math.max(0.1, parseFloat(stake) || 1),
              multiplier: 100,
              trail_pct: 5,
              entry_mode: entryMode,
              rsi_confirm: rsiConfirm,
              htf_trend: htfTrend,
              auto_blacklist: autoBlacklist,
              auto_disable_losing_strategies: autoDisableLosing,
              accu_tp_pct: Math.max(3, Math.min(40, parseInt(accuTpPct, 10) || 20)),
              max_trades: Math.max(0, parseInt(maxTrades, 10) || 0),
              max_concurrent: Math.max(0, parseInt(maxConcurrent, 10) || 0),
              daily_stop_loss: Math.max(0, parseFloat(dailySL) || 0),
              daily_take_profit: Math.max(0, parseFloat(dailyTP) || 0),
            };
            await api.post("/bot/start", {
              account_id: selectedAccount,
              access_token: accessToken,
              disclaimer_accepted: true,
              config: cfg,
            });
            toast.success("Bot resumed");
            fetchStatus();
          } catch (e) {
            toast.error("Auto-resume failed — tap Start in Cloud", {
              description: e?.response?.data?.detail || e.message || "",
            });
          }
        })();
      } else {
        toast("Cloud Bot stopped", { description: reason });
      }
    }
    if (!reason) lastReasonRef.current = null;
    // Reset attempt flag when bot is actively running so a future restart
    // can re-trigger the auto-resume.
    if (status?.is_running) autoResumeAttemptedRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.stop_reason, status?.is_running, selectedAccount]);

  // Per-account lock so concurrent /api/bot/reconcile calls from the same
  // user can't double-process the same trade. 60s throttle.
  const lastReconcileRef = useMemo(() => ({ current: 0 }), []);

  // Auto-reconcile stale-open trades. Runs when (a) bot is idle, (b) we
  // have open trades, (c) the user has an access token, (d) last reconcile
  // was >60s ago. Keeps the DB in sync with Deriv without forcing the user
  // to tap Start.
  useEffect(() => {
    if (status?.is_running) return;
    if (!accessToken || !selectedAccount) return;
    const hasOpen = trades.some((t) => t.status === "open");
    if (!hasOpen) return;
    const now = Date.now();
    if (now - lastReconcileRef.current < 60_000) return;
    lastReconcileRef.current = now;
    (async () => {
      try {
        const res = await api.post("/bot/reconcile", {
          account_id: selectedAccount,
          access_token: accessToken,
        });
        const updated = res?.data?.updated || 0;
        if (updated > 0) {
          toast.success(`Reconciled ${updated} trade${updated > 1 ? "s" : ""}`, {
            description: "Updated status from Deriv.",
          });
          // Refresh local state immediately
          fetchTrades();
          fetchSymbolStats();
        }
      } catch {
        // silent — reconcile is opportunistic
      }
    })();
  }, [status?.is_running, trades, accessToken, selectedAccount, lastReconcileRef, fetchTrades, fetchSymbolStats]);

  const handleStart = () => {
    if (!selectedAccount || !accessToken) {
      toast.error("Please sign in with Deriv first");
      return;
    }
    // MasterSpec §B — show the risk disclaimer modal on every start.
    setDisclaimerChecked(false);
    setDisclaimerOpen(true);
  };

  const handleConfirmStart = async () => {
    if (!disclaimerChecked) return;
    setDisclaimerOpen(false);
    setLoading(true);
    try {
      const cfg = {
        strategy,
        rf_duration: Math.max(1, parseInt(rfDuration, 10) || 5),
        rf_duration_unit: "m",
        risk_mode: riskMode,
        risk_pct: parseFloat(riskPct) || 2,
        stake: Math.max(0.1, parseFloat(stake) || 1),
        multiplier: 100,
        trail_pct: 5,
        entry_mode: entryMode,
        rsi_confirm: rsiConfirm,
        htf_trend: htfTrend,
        auto_blacklist: autoBlacklist,
        auto_disable_losing_strategies: autoDisableLosing,
        accu_tp_pct: Math.max(3, Math.min(40, parseInt(accuTpPct, 10) || 20)),
        max_trades: Math.max(0, parseInt(maxTrades, 10) || 0),
        max_concurrent: Math.max(0, parseInt(maxConcurrent, 10) || 0),
        daily_stop_loss: Math.max(0, parseFloat(dailySL) || 0),
        daily_take_profit: Math.max(0, parseFloat(dailyTP) || 0),
      };
      const res = await api.post(`/bot/start`, {
        account_id: selectedAccount,
        access_token: accessToken,
        disclaimer_accepted: true,
        config: cfg,
      }, { headers: { Authorization: `Bearer ${accessToken}` } });
      if (res.data?.ok) {
        toast.success(res.data.already_running ? "Bot was already running" : "Cloud bot started", {
          description: "It will keep running on the server until you stop it.",
        });
        fetchStatus();
      } else {
        toast.error("Failed to start cloud bot");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Start failed");
    } finally {
      setLoading(false);
    }
  };

  const handleStop = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      await api.post(`/bot/stop/${encodeURIComponent(selectedAccount)}`);
      toast.success("Cloud bot stopped");
      fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Stop failed");
    } finally {
      setLoading(false);
    }
  };

  const handleUnpauseStrategy = async (strategy) => {
    if (!selectedAccount) return;
    try {
      await api.post(
        `/bot/unpause-strategy/${encodeURIComponent(selectedAccount)}`,
        { strategy },
      );
      toast.success(`Resumed "${STRATEGY_LABELS[strategy] || strategy}"`, {
        description: "Stats window cleared — will re-pause if it bleeds again.",
      });
      fetchStatus();
      fetchStrategyStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Resume failed");
    }
  };

  const handleResetStats = async () => {
    if (!selectedAccount) return;
    if (status?.is_running) {
      toast.error("Stop the bot first before resetting stats");
      return;
    }
    const ok = window.confirm(
      "Reset all Cloud Bot stats and clear the strategy/symbol leaderboards for this account?\n\n" +
      "This zeroes Trades Placed, Run P&L, and removes all bot_trades rows so you can test from scratch.\n\n" +
      "Your /history trade log is NOT touched."
    );
    if (!ok) return;
    setLoading(true);
    try {
      const res = await api.post(
        `/bot/reset-stats/${encodeURIComponent(selectedAccount)}`,
      );
      const removed = res.data?.trades_deleted ?? 0;
      toast.success("Stats reset", {
        description: `Counters zeroed${removed ? ` · ${removed} trades cleared` : ""}.`,
      });
      // Local clear so the UI updates instantly without waiting for the poll
      setTrades([]);
      setSymbolStats([]);
      setStrategyStats([]);
      fetchStatus();
      fetchTrades();
      fetchSymbolStats();
      fetchStrategyStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  const running = status?.is_running;
  const stats = status?.stats || {};
  const log = useMemo(() => (status?.log || []).slice().reverse(), [status?.log]);
  const livePreview = balance != null && riskMode === "percent"
    ? ((balance * riskPct) / 100).toFixed(2)
    : null;

  return (
    <div data-testid="cloud-bot-panel" className="vts-card p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="relative flex h-2.5 w-2.5">
          {running && (
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-vts-profit opacity-60"></span>
          )}
          <span className={`relative inline-flex rounded-full h-2.5 w-2.5 ${running ? "bg-vts-profit" : "bg-vts-silver/40"}`}></span>
        </span>
        <h3 className="text-white font-heading font-semibold text-base">
          Cloud Bot
        </h3>
        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-vts-blue/15 text-vts-blue border border-vts-blue/30 font-mono">
          24/7
        </span>
        <span className="text-[11px] text-vts-silver/60 ml-auto font-mono">
          {running ? "RUNNING — close phone, bot keeps trading" : "Idle"}
        </span>
        <button
          data-testid="cloud-bot-refresh"
          onClick={fetchStatus}
          title="Refresh status"
          className="text-vts-silver/60 hover:text-white p-1"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      <p className="text-[11px] text-vts-silver/70 mb-4 leading-relaxed">
        Runs the FX Hunter strategy on our server, not your phone.
        Start it, close your phone, sleep — bot keeps trading. Open the app
        any time to see live progress.
      </p>

      {/* Protection summary — show the user this isn't naive trading */}
      <div className="mb-4 px-3 py-2 bg-vts-profit/5 border border-vts-profit/20 rounded-md text-[11px] font-mono leading-relaxed">
        <span className="text-vts-profit font-semibold">🔒 Profit Protection ON</span>
        <span className="text-vts-silver/80"> · −10% hard stop-loss · arms trail at +8% · locks 4% behind peak · auto-sells on retrace</span>
      </div>

      {isDemo && (
        <div className="mb-4 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-md text-[11px] text-amber-300 flex items-start gap-2">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            You're on a demo account ({accountId}). The Cloud Bot works on demo too — useful to test.
            Switch to a real account in the header to trade real markets.
          </span>
        </div>
      )}

      {/* Account picker */}
      {accounts.length > 0 && (
        <div className="mb-3">
          <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1.5 block font-mono">
            Account
          </Label>
          <select
            data-testid="cloud-bot-account"
            value={selectedAccount}
            disabled={running || loading}
            onChange={(e) => setSelectedAccount(e.target.value)}
            className="w-full bg-vts-surface border border-vts-line rounded-md px-3 py-2 text-sm text-white font-mono disabled:opacity-50"
          >
            {accounts.map((a) => {
              const id = a.loginid || a.account_id;
              // For the currently-selected account, prefer the LIVE balance
              // prop (polled every 15s) over the stale OAuth-time value
              // cached on the account object. Avoids the "$16 in dropdown,
              // $14 in stake preview" mismatch users would otherwise see.
              const liveBal = id === accountId && balance != null ? balance : a.balance;
              const balDisplay = liveBal != null && liveBal !== "?" ? Number(liveBal).toFixed(2) : "?";
              return (
                <option key={id} value={id}>
                  {id} · {balDisplay} {a.currency || "USD"}
                </option>
              );
            })}
          </select>
        </div>
      )}

      {/* Strategy selector — chooses which contract type + signal logic
          the Cloud Bot uses. */}
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1.5 block font-mono">
          Strategy
        </Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            // June 2026 — Multiplier chips RESTORED (Deriv support confirmed in
            // writing that MULTUP/MULTDOWN earn app markup like other options).
            { id: "multiplier", label: "Multiplier", sub: "EMA cross · ×100 trail" },
            { id: "rise_fall", label: "Rise/Fall", sub: "EMA cross · 5min + 15min" },
            { id: "both", label: "Both", sub: "Mult + R/F · 2× action" },
            { id: "all", label: "All (Parallel)", sub: "R/F + Multiplier + ACCU + NoTouch + BRR" },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              disabled={running || loading}
              onClick={() => setStrategy(s.id)}
              data-testid={`cloud-bot-strategy-${s.id}`}
              className={`py-2 px-2 rounded text-center transition border ${
                strategy === s.id
                  ? "bg-vts-blue text-white border-vts-blue"
                  : "bg-vts-surface text-vts-silver/70 border-vts-line hover:text-white"
              } disabled:opacity-50`}
            >
              <div className="font-mono text-[11px] uppercase tracking-wider font-semibold">{s.label}</div>
              <div className="text-[9px] font-mono opacity-70 mt-0.5">{s.sub}</div>
            </button>
          ))}
        </div>
        {(strategy === "rise_fall" || strategy === "both") && (
          <div className="mt-2 flex items-center gap-2">
            <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono whitespace-nowrap">
              R/F duration (min)
            </Label>
            <Input
              data-testid="cloud-bot-rf-duration"
              type="number"
              min={3}
              max={60}
              step={1}
              value={rfDuration}
              disabled={running || loading}
              onChange={(e) => setRfDuration(e.target.value)}
              className="bg-vts-surface border-vts-line text-white font-mono text-sm w-20"
            />
          </div>
        )}
        {/* Feb 2026 v7.3 — ACCU Take-Profit dial REMOVED per user request:
            "I am seeing some ACC like take print percentage on the bot tab
            please remove it". ACCU was removed from the parallel ladder
            back in v4 (Feb 2026) so this dial only mattered for the
            single-strategy `accu` mode — which users rarely select. The
            backend constant accu_tp_pct still defaults to 20% so single-
            strategy ACCU runs keep working without UI clutter. */}
      </div>

      {/* Entry-mode toggle — controls signal quality vs trade frequency. */}
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1.5 block font-mono">
          Entry signal
        </Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            disabled={running || loading}
            onClick={() => setEntryMode("closed")}
            data-testid="cloud-bot-entry-closed"
            className={`py-2 rounded font-mono text-[11px] uppercase tracking-wider transition border ${
              entryMode === "closed"
                ? "bg-vts-blue text-white border-vts-blue"
                : "bg-vts-surface text-vts-silver/70 border-vts-line"
            } disabled:opacity-50`}
          >
            Confirmed (M1 close)
          </button>
          <button
            type="button"
            disabled={running || loading}
            onClick={() => setEntryMode("forming")}
            data-testid="cloud-bot-entry-forming"
            className={`py-2 rounded font-mono text-[11px] uppercase tracking-wider transition border ${
              entryMode === "forming"
                ? "bg-vts-blue text-white border-vts-blue"
                : "bg-vts-surface text-vts-silver/70 border-vts-line"
            } disabled:opacity-50`}
          >
            Aggressive (live)
          </button>
        </div>
        <p className="text-[10px] text-vts-silver/50 mt-1.5 font-mono leading-relaxed">
          {entryMode === "closed"
            ? "Waits for the M1 candle to close before firing — fewer, higher-quality trades."
            : "Fires the moment EMAs cross on the live tick — more trades, more phantoms."}
        </p>
        {/* RSI(14) confirmation — extra filter on top of the EMA cross */}
        <label className="mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-vts-bg/40 border border-vts-line rounded-md cursor-pointer hover:border-vts-blue/40 transition">
          <span className="text-[11px] font-mono text-vts-silver/80">
            RSI(14) confirmation
            <span className="block text-[9px] text-vts-silver/50 leading-relaxed mt-0.5">
              Only fires if RSI agrees with the cross direction
            </span>
          </span>
          <input
            type="checkbox"
            checked={rsiConfirm}
            disabled={running || loading}
            onChange={(e) => setRsiConfirm(e.target.checked)}
            data-testid="cloud-bot-rsi-confirm"
            className="h-4 w-4 accent-vts-blue cursor-pointer disabled:opacity-50"
          />
        </label>
        {/* M5 higher-timeframe trend alignment — filters bad trades across all pairs */}
        <label className="mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-vts-bg/40 border border-vts-line rounded-md cursor-pointer hover:border-vts-blue/40 transition">
          <span className="text-[11px] font-mono text-vts-silver/80">
            M5 trend alignment
            <span className="block text-[9px] text-vts-silver/50 leading-relaxed mt-0.5">
              Only fires when the M5 trend agrees with the M1 cross
            </span>
          </span>
          <input
            type="checkbox"
            checked={htfTrend}
            disabled={running || loading}
            onChange={(e) => setHtfTrend(e.target.checked)}
            data-testid="cloud-bot-htf-trend"
            className="h-4 w-4 accent-vts-blue cursor-pointer disabled:opacity-50"
          />
        </label>
        {/* Auto-blacklist losing symbols — uses the per-symbol leaderboard data */}
        <label className="mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-vts-bg/40 border border-vts-line rounded-md cursor-pointer hover:border-vts-blue/40 transition">
          <span className="text-[11px] font-mono text-vts-silver/80">
            Auto-blacklist losers
            <span className="block text-[9px] text-vts-silver/50 leading-relaxed mt-0.5">
              Skip pairs with &lt; 40% win-rate over 10+ closed trades
            </span>
          </span>
          <input
            type="checkbox"
            checked={autoBlacklist}
            disabled={running || loading}
            onChange={(e) => setAutoBlacklist(e.target.checked)}
            data-testid="cloud-bot-auto-blacklist"
            className="h-4 w-4 accent-vts-blue cursor-pointer disabled:opacity-50"
          />
        </label>
        {/* Auto-disable losing strategies — only meaningful in 'both' / 'all' */}
        <label
          className={`mt-2 flex items-center justify-between gap-2 px-3 py-2 bg-vts-bg/40 border border-vts-line rounded-md cursor-pointer hover:border-vts-blue/40 transition ${
            strategy !== "both" && strategy !== "all" ? "opacity-60" : ""
          }`}
        >
          <span className="text-[11px] font-mono text-vts-silver/80">
            Auto-pause losing strategies
            <span className="block text-[9px] text-vts-silver/50 leading-relaxed mt-0.5">
              Pause any strategy under 40% WR after 10 trades · keeps others running
              {strategy !== "both" && strategy !== "all" && (
                <span className="text-vts-silver/40"> · enable Both/All to use</span>
              )}
            </span>
          </span>
          <input
            type="checkbox"
            checked={autoDisableLosing}
            disabled={running || loading}
            onChange={(e) => setAutoDisableLosing(e.target.checked)}
            data-testid="cloud-bot-auto-disable-losing"
            className="h-4 w-4 accent-vts-blue cursor-pointer disabled:opacity-50"
          />
        </label>
      </div>

      {/* Feb 2026 v7.3 — Risk sizing. DIFFERS locked-stake widget removed
          per user request: "I also remove the T1 STARTER the differs cycle
          it dont needed". Backend tier system still applies the locked base
          stake automatically when strategy=differs; the UI just no longer
          shows it as a separate panel. */}
      <div className="mb-3 grid grid-cols-2 gap-2">
        <button
          type="button"
          disabled={running || loading}
          onClick={() => setRiskMode("fixed")}
          className={`py-2 rounded font-mono text-xs uppercase tracking-wider transition ${
            riskMode !== "percent" ? "bg-vts-blue text-white" : "bg-vts-surface text-vts-silver/70"
          } disabled:opacity-50`}
        >
          Fixed amount
        </button>
        <button
          type="button"
          disabled={running || loading}
          onClick={() => setRiskMode("percent")}
          className={`py-2 rounded font-mono text-xs uppercase tracking-wider transition ${
            riskMode === "percent" ? "bg-vts-blue text-white" : "bg-vts-surface text-vts-silver/70"
          } disabled:opacity-50`}
        >
          % of balance
        </button>
      </div>

      {riskMode === "percent" ? (
        <>
          <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1.5 block font-mono">
            Risk per trade
          </Label>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[1, 2, 3, 5, 10].map((n) => (
              <button
                key={n}
                type="button"
                disabled={running || loading}
                onClick={() => setRiskPct(n)}
                className={`py-2 rounded-md font-mono text-sm font-semibold transition border ${
                  riskPct === n
                    ? "bg-vts-blue text-white border-vts-blue"
                    : "bg-vts-surface text-vts-silver/70 border-vts-line"
                } disabled:opacity-50`}
              >
                {n}%
              </button>
            ))}
          </div>
          <Input
            data-testid="cloud-bot-risk-input"
            type="number"
            min={0.1}
            max={10}
            step={0.5}
            value={riskPct}
            disabled={running || loading}
            onChange={(e) => setRiskPct(Math.max(0.1, Math.min(10, parseFloat(e.target.value) || 2)))}
            className="bg-vts-surface border-vts-line text-white font-mono text-sm"
          />
          {livePreview && (
            <div className="mt-2 px-3 py-2 bg-vts-profit/5 border border-vts-profit/20 rounded-md text-[11px] font-mono flex justify-between">
              <span className="text-vts-silver/70">Stake @ {riskPct}% of {balance?.toFixed?.(2) ?? balance}</span>
              <span className="text-vts-profit font-semibold">~{livePreview} {currency || "USD"}</span>
            </div>
          )}
        </>
      ) : (
        <>
          <Label className="text-[10px] uppercase tracking-wider text-vts-silver/60 mb-1.5 block font-mono">
            Stake per trade ({currency || "USD"})
          </Label>
          <div className="grid grid-cols-5 gap-2 mb-2">
            {[1, 2, 5, 10, 25].map((n) => (
              <button
                key={n}
                type="button"
                disabled={running || loading}
                onClick={() => setStake(n)}
                className={`py-2 rounded-md font-mono text-sm font-semibold transition border ${
                  Number(stake) === n
                    ? "bg-vts-blue text-white border-vts-blue"
                    : "bg-vts-surface text-vts-silver/70 border-vts-line"
                } disabled:opacity-50`}
              >
                ${n}
              </button>
            ))}
          </div>
          <Input
            data-testid="cloud-bot-stake-input"
            type="number"
            min={0.1}
            step={0.5}
            value={stake}
            disabled={running || loading}
            onChange={(e) => {
              // Allow free typing; only clamp on blur via parseFloat so the
              // user can clear & retype without us forcing back to 1.
              const raw = e.target.value;
              if (raw === "") {
                setStake("");
                return;
              }
              const v = parseFloat(raw);
              setStake(Number.isFinite(v) && v >= 0 ? v : "");
            }}
            onBlur={(e) => {
              const v = parseFloat(e.target.value);
              if (!Number.isFinite(v) || v <= 0) setStake(1);
            }}
            className="bg-vts-surface border-vts-line text-white font-mono text-sm"
            placeholder="Custom amount"
          />
          <p className="text-[10px] text-vts-silver/50 mt-1.5 font-mono leading-relaxed">
            Bot will use this exact amount on every trade. Deriv may auto-bump
            it if the pair has a higher minimum (you'll see "🔧 Bumped …" in the log).
          </p>
        </>
      )}

      {/* Advanced safety caps — optional, defaults to unlimited. Lets users
          safely leave the bot running overnight without burning the account
          if the strategy goes cold. */}
      <details className="mt-4 group" data-testid="cloud-bot-limits">
        <summary className="cursor-pointer text-[11px] font-mono uppercase tracking-wider text-vts-silver/70 hover:text-white select-none flex items-center gap-2">
          <span className="inline-block transition-transform group-open:rotate-90">▶</span>
          Safety limits (optional)
        </summary>
        <div className="mt-3 space-y-3">
          <p className="text-[10px] font-mono text-vts-silver/50 leading-relaxed">
            Leave blank for unlimited. Bot stops automatically when ANY cap is hit. P&amp;L tracking resets every time you press Start.
          </p>
          {/* Concurrent-trade cap — separate row so it stands out. Limits how
              many open contracts the bot can run at once across all pairs. */}
          <div>
            <Label className="text-[9px] uppercase tracking-wider text-vts-silver/60 mb-1 block font-mono">
              Max concurrent open trades
            </Label>
            <Input
              data-testid="cloud-bot-max-concurrent"
              type="number"
              min={0}
              step={1}
              value={maxConcurrent}
              placeholder="6"
              disabled={running || loading}
              onChange={(e) => setMaxConcurrent(e.target.value)}
              className="bg-vts-surface border-vts-line text-white font-mono text-sm"
            />
            <p className="text-[9px] text-vts-silver/40 mt-1 font-mono">
              Bot pauses new entries when this many trades are open. 0 = unlimited.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-[9px] uppercase tracking-wider text-vts-silver/60 mb-1 block font-mono">
                Max trades
              </Label>
              <Input
                data-testid="cloud-bot-max-trades"
                type="number"
                min={0}
                step={1}
                value={maxTrades}
                placeholder="∞"
                disabled={running || loading}
                onChange={(e) => setMaxTrades(e.target.value)}
                className="bg-vts-surface border-vts-line text-white font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-wider text-vts-silver/60 mb-1 block font-mono">
                Daily SL ($)
              </Label>
              <Input
                data-testid="cloud-bot-daily-sl"
                type="number"
                min={0}
                step={1}
                value={dailySL}
                placeholder="off"
                disabled={running || loading}
                onChange={(e) => setDailySL(e.target.value)}
                className="bg-vts-surface border-vts-line text-white font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-[9px] uppercase tracking-wider text-vts-silver/60 mb-1 block font-mono">
                Daily TP ($)
              </Label>
              <Input
                data-testid="cloud-bot-daily-tp"
                type="number"
                min={0}
                step={1}
                value={dailyTP}
                placeholder="off"
                disabled={running || loading}
                onChange={(e) => setDailyTP(e.target.value)}
                className="bg-vts-surface border-vts-line text-white font-mono text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: "Conservative", mt: 20, sl: 5, tp: 10 },
              { label: "Balanced", mt: 50, sl: 10, tp: 25 },
              { label: "Aggressive", mt: 0, sl: 25, tp: 75 },
            ].map((p) => (
              <button
                key={p.label}
                type="button"
                disabled={running || loading}
                onClick={() => {
                  setMaxTrades(p.mt ? String(p.mt) : "");
                  setDailySL(p.sl ? String(p.sl) : "");
                  setDailyTP(p.tp ? String(p.tp) : "");
                  toast(`Preset: ${p.label}`, {
                    description: `${p.mt || "∞"} trades · −$${p.sl} / +$${p.tp}`,
                  });
                }}
                className="py-1.5 rounded-md text-[10px] font-mono uppercase tracking-wider border border-vts-line bg-vts-surface text-vts-silver/80 hover:text-white hover:border-vts-blue disabled:opacity-50"
                data-testid={`cloud-bot-preset-${p.label.toLowerCase()}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </details>

      {/* Stake Sanity Preview — Feb 2026. Shows the actual $-amount that
          will fire for each strategy in the current "All (Parallel)" /
          "Both" ladder. Catches stake-config bugs BEFORE Start (e.g.
          percent mode silently using risk_pct instead of the typed
          stake, or ACCU ignoring stake config — both real Feb 2026
          incidents). Only renders when the bot is idle (config editable)
          and a strategy with multiple firing tiers is selected. */}
      {!running && (strategy === "all" || strategy === "both" || strategy === "accu" || strategy === "rise_fall") && (() => {
        const bal = Number(balance) || 0;
        const pct = Math.max(0.1, Math.min(10, Number(riskPct) || 2));
        const fixedStake = Math.max(0.1, Number(stake) || 1);
        // Effective per-trade $ using the same math the backend applies.
        let effective;
        let reason;
        if (riskMode === "percent" && bal > 0) {
          effective = Math.max(1, Math.round((bal * pct) / 100 * 100) / 100);
          reason = `${pct}% of $${bal.toFixed(2)} balance`;
        } else if (riskMode === "percent") {
          effective = fixedStake;
          reason = "balance loading… using fixed fallback";
        } else {
          effective = fixedStake;
          reason = "fixed stake (WYSIWYG)";
        }
        // ACCU shares R/F sizing but is clamped to the safety ceiling
        // max(2×stake, 5% of balance).
        const ceiling = bal > 0 ? Math.max(fixedStake * 2, bal * 0.05) : Infinity;
        const accuStake = Math.min(effective, ceiling);
        const tiers = [];
        if (strategy === "all" || strategy === "both" || strategy === "rise_fall") {
          tiers.push({ k: "R/F 5m", v: effective });
          if (strategy !== "rise_fall") tiers.push({ k: "R/F 15m", v: effective });
        }
        if (strategy === "all" || strategy === "both") {
          tiers.push({ k: "NoTouch", v: effective });
        }
        if (strategy === "all" || strategy === "accu") {
          tiers.push({ k: "ACCU", v: accuStake });
        }
        return (
          <div
            data-testid="cloud-bot-stake-preview"
            className="mt-4 px-3 py-2.5 bg-vts-bg/60 border border-vts-blue/40 rounded-md"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="text-[10px] uppercase tracking-wider font-mono text-vts-blue">
                Stake preview · next trade batch
              </div>
              <div className="text-[9px] font-mono text-vts-silver/50">{reason}</div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              {tiers.map((t) => (
                <div
                  key={t.k}
                  className="flex items-center justify-between text-[11px] font-mono"
                  data-testid={`cloud-bot-stake-preview-row-${t.k.toLowerCase().replace(/[\s/]/g, "-")}`}
                >
                  <span className="text-vts-silver/70">{t.k}</span>
                  <span className="text-vts-profit font-semibold tabular-nums">
                    ${Number(t.v).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>
            {riskMode === "percent" && (
              <p className="text-[9px] text-vts-silver/45 font-mono mt-1.5 leading-snug">
                Stake auto-scales with balance. Switch to <span className="text-vts-blue">Fixed amount</span> for a constant $-value per trade.
              </p>
            )}
          </div>
        );
      })()}

      {/* Start / Stop */}
      <div className="mt-4">
        {running ? (
          <Button
            data-testid="cloud-bot-stop"
            onClick={handleStop}
            disabled={loading}
            className="w-full bg-vts-loss hover:bg-vts-loss/90 text-white font-mono font-semibold py-3"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Square className="h-4 w-4 mr-2 fill-white" />}
            Stop Cloud Bot
          </Button>
        ) : (
          <Button
            data-testid="cloud-bot-start"
            onClick={handleStart}
            disabled={loading || !selectedAccount}
            className="w-full bg-vts-profit hover:bg-vts-profit/90 text-white font-mono font-semibold py-3"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Cloud className="h-4 w-4 mr-2" />}
            Start in Cloud (24/7)
          </Button>
        )}
        {/* Reset stats — visible only when the bot is idle. Lets the user
            zero out Trades Placed / Run P&L / strategy + symbol leaderboards
            so they can test a strategy from scratch without history bleeding
            into the next run. */}
        {!running && (
          <button
            type="button"
            data-testid="cloud-bot-reset-stats"
            onClick={handleResetStats}
            disabled={loading || !selectedAccount}
            className="w-full mt-2 py-2 text-[11px] font-mono uppercase tracking-wider text-vts-silver/60 hover:text-vts-loss hover:bg-vts-loss/10 border border-vts-line rounded transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reset stats &amp; clear trades
          </button>
        )}
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 bg-vts-loss/10 border border-vts-loss/30 rounded-md text-[11px] font-mono text-vts-loss">
          {error}
        </div>
      )}

      {/* MasterSpec §F — Tier badge + cycle progress + DIFFERS state + daily target.
          Renders only when the bot is/was running and the backend has emitted
          a tier (i.e. balance ≥ $50). */}
      {status?.current_tier && (() => {
        const tier = status.current_tier;
        const meta = TIER_BADGE[tier.id] || TIER_BADGE[3];
        const pnl = Number(stats.pnl) || 0;
        const targetLo = Number(tier.target_lo) || 0;
        const targetHi = Number(tier.target_hi) || 0;
        const pct = targetHi > 0 ? Math.max(0, Math.min(100, (pnl / targetHi) * 100)) : 0;
        const nextDeposit = TIER_NEXT_DEPOSIT[tier.id];
        return (
          <div className="mt-4 space-y-2" data-testid="cloud-bot-tier-strip">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <span
                  data-testid="cloud-bot-tier-badge"
                  className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border ${meta.color}`}
                >
                  T{tier.id} · {tier.name}
                </span>
                <span className="text-[10px] font-mono text-vts-silver/60">
                  base ${Number(tier.base).toFixed(2)} · EMA ${Number(tier.ema_stake).toFixed(2)}
                </span>
              </div>
              <div className="text-[10px] font-mono text-vts-silver/50">
                cycle wins {tier.wins} · daily SL ${tier.daily_sl}
              </div>
            </div>
            {/* DIFFERS state row — driven by live stats from /bot/status. */}
            {(() => {
              const dStat = status?.differs;
              const isActive = !!dStat;
              const state = dStat?.state || "OFF";
              const wins = dStat?.win_count ?? 0;
              const target = dStat?.wins_target ?? Number(tier.wins) ?? 4;
              const stateClr = state === "NORMAL" ? "bg-vts-blue/15 text-vts-blue border-vts-blue/40"
                : state === "RECOVERY" ? "bg-amber-500/20 text-amber-300 border-amber-500/50"
                : state === "PAUSED" ? "bg-vts-loss/20 text-vts-loss border-vts-loss/40"
                : "bg-vts-silver/10 text-vts-silver/60 border-vts-line/60";
              return (
                <div
                  data-testid="cloud-bot-differs-state"
                  className="flex items-center justify-between px-3 py-1.5 bg-vts-bg/60 border border-vts-line/60 rounded text-[11px] font-mono"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-vts-silver/70">DIFFERS Cycle</span>
                    {isActive && (
                      <span className="text-[10px] text-vts-silver/50">
                        · win {wins}/{target}
                        {dStat?.daily_pnl !== undefined && (
                          <> · day{" "}
                            <span className={Number(dStat.daily_pnl) >= 0 ? "text-vts-profit" : "text-vts-loss"}>
                              {Number(dStat.daily_pnl) >= 0 ? "+" : ""}${Number(dStat.daily_pnl).toFixed(2)}
                            </span>
                          </>
                        )}
                      </span>
                    )}
                  </div>
                  <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded border ${stateClr}`}>
                    {isActive ? state : "Not active in this run"}
                  </span>
                </div>
              );
            })()}
            {/* Daily target progress bar. */}
            {targetHi > 0 && (
              <div data-testid="cloud-bot-daily-target">
                <div className="flex items-center justify-between text-[10px] font-mono text-vts-silver/60 mb-1">
                  <span>Daily target ${targetLo}–${targetHi}</span>
                  <span className={pnl >= 0 ? "text-vts-profit" : "text-vts-loss"}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                  </span>
                </div>
                <div className="h-1.5 bg-vts-bg/80 border border-vts-line rounded overflow-hidden">
                  <div
                    className={pnl >= 0 ? "h-full bg-vts-profit/70" : "h-full bg-vts-loss/70"}
                    style={{ width: `${pnl >= 0 ? pct : Math.min(100, Math.abs((pnl / targetHi) * 100))}%` }}
                  />
                </div>
              </div>
            )}
            {/* Upgrade prompt with exact deposit amount. */}
            {nextDeposit !== null && (
              <div
                data-testid="cloud-bot-upgrade-prompt"
                className="px-3 py-1.5 text-[10px] font-mono text-vts-silver/60 bg-vts-blue/5 border border-vts-blue/20 rounded"
              >
                Deposit ${nextDeposit - (balance || tier.min) > 0 ? Math.ceil(nextDeposit - (balance || tier.min)) : nextDeposit}
                {" "}more to unlock the next tier with larger stakes &amp; daily target.
              </div>
            )}
          </div>
        );
      })()}

      {/* Stats */}
      <div className="mt-4 grid grid-cols-3 gap-2">
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-3 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Trades placed</div>
          <div className="text-base font-mono text-white tabular-nums">{stats.placed || 0}</div>
        </div>
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-3 py-2 text-center" data-testid="cloud-bot-run-pnl">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Run P&amp;L</div>
          <div className={`text-base font-mono tabular-nums ${
            Number(stats.pnl) > 0 ? "text-vts-profit" : Number(stats.pnl) < 0 ? "text-vts-loss" : "text-white"
          }`}>
            {Number.isFinite(Number(stats.pnl)) ? `${Number(stats.pnl) >= 0 ? "+" : ""}$${Number(stats.pnl).toFixed(2)}` : "$0.00"}
          </div>
          {/* v8.5 — unrealised P&L from open contracts.
              `trades` is the latest 25-row recent-trades feed; the
              reconciler writes `current_profit` into each open row
              every 20s. Sum those for a live "open" badge so the
              user can see the moving P&L from open positions before
              they settle. Realized + open = total exposure. */}
          {(() => {
            const openTrades = (trades || []).filter((t) => t.status === "open");
            if (openTrades.length === 0) return null;
            const openPL = openTrades.reduce(
              (sum, t) => sum + (Number(t.current_profit) || 0),
              0,
            );
            const cls = openPL > 0 ? "text-vts-profit/80" : openPL < 0 ? "text-vts-loss/80" : "text-vts-silver/60";
            return (
              <div
                data-testid="cloud-bot-open-pnl"
                className={`text-[10px] font-mono tabular-nums mt-0.5 ${cls}`}
                title={`Unrealised P&L from ${openTrades.length} open contract(s)`}
              >
                {openPL >= 0 ? "+" : ""}${openPL.toFixed(2)} <span className="text-[8px] uppercase tracking-wider opacity-60">open</span>
              </div>
            );
          })()}
        </div>
        <div className="bg-vts-bg/60 border border-vts-line rounded-md px-3 py-2 text-center">
          <div className="text-[9px] font-mono uppercase tracking-wider text-vts-silver/60">Status</div>
          <div className={`text-base font-mono tabular-nums ${running ? "text-vts-profit" : "text-vts-silver"}`}>
            {status?.status || "idle"}
          </div>
        </div>
      </div>

      {/* Signals Filtered (last 24h). OPERATOR-ONLY: technical diagnostic. */}
      {operatorMode && running && status?.filter_stats && (status.filter_stats.scanned > 0) && (() => {
        const fs = status.filter_stats;
        const scanned = Number(fs.scanned) || 0;
        const fired = Number(fs.fired) || 0;
        const rejected = scanned - fired;
        const reject_pct = scanned ? (rejected / scanned) * 100 : 0;
        const fire_pct = scanned ? (fired / scanned) * 100 : 0;
        // Rank rejection reasons by count so the biggest blocker leads.
        const reasons = [
          { k: "trend", label: "stack/trend", v: Number(fs.rejected_trend) || 0 },
          { k: "momentum", label: "momentum", v: Number(fs.rejected_momentum) || 0 },
          { k: "adx", label: "chop", v: Number(fs.rejected_adx) || 0 },
          { k: "score", label: "score", v: Number(fs.rejected_score) || 0 },
          { k: "strength", label: "strength", v: Number(fs.rejected_strength) || 0 },
          { k: "other", label: "other", v: Number(fs.rejected_other) || 0 },
        ].filter((r) => r.v > 0).sort((a, b) => b.v - a.v);
        // Friendly window label
        let windowLabel = "since run start";
        if (fs.window_start) {
          try {
            const ws = new Date(fs.window_start);
            const ageMin = (Date.now() - ws.getTime()) / 60000;
            if (ageMin < 60) windowLabel = `last ${Math.max(1, Math.round(ageMin))} min`;
            else if (ageMin < 1440) windowLabel = `last ${Math.round(ageMin / 60)} h`;
            else windowLabel = "rolling 24h";
          } catch {
            // fallback
          }
        }
        return (
          <div
            data-testid="cloud-bot-filter-stats"
            className="mt-4 px-3 py-2.5 bg-vts-bg/60 border border-vts-blue/30 rounded-md"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider font-mono text-vts-blue">
                Signals filtered · {windowLabel}
              </div>
              <div className="text-[9px] font-mono text-vts-silver/50">
                {fire_pct.toFixed(0)}% fire rate
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-2">
              <div className="bg-vts-surface/60 rounded px-2 py-1.5 text-center">
                <div className="text-[8px] uppercase tracking-wider text-vts-silver/50 font-mono">
                  scanned
                </div>
                <div className="text-sm font-mono tabular-nums text-white">
                  {scanned}
                </div>
              </div>
              <div className="bg-vts-profit/10 rounded px-2 py-1.5 text-center border border-vts-profit/20">
                <div className="text-[8px] uppercase tracking-wider text-vts-profit/80 font-mono">
                  fired
                </div>
                <div className="text-sm font-mono tabular-nums text-vts-profit">
                  {fired}
                </div>
              </div>
              <div className="bg-vts-loss/10 rounded px-2 py-1.5 text-center border border-vts-loss/20">
                <div className="text-[8px] uppercase tracking-wider text-vts-loss/80 font-mono">
                  rejected
                </div>
                <div className="text-sm font-mono tabular-nums text-vts-loss">
                  {rejected}
                </div>
              </div>
            </div>
            {reasons.length > 0 && (
              <>
                <div className="flex flex-wrap gap-1">
                  {reasons.map((r) => (
                    <span
                      key={r.k}
                      data-testid={`cloud-bot-filter-reason-${r.k}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono bg-vts-surface/80 border border-vts-line"
                    >
                      <span className="text-vts-silver/60">{r.label}</span>
                      <span className="text-vts-loss tabular-nums font-semibold">
                        {r.v}
                      </span>
                    </span>
                  ))}
                </div>
                <p className="text-[9px] text-vts-silver/45 font-mono mt-1.5 leading-snug">
                  {reject_pct.toFixed(0)}% rejection rate is healthy — proves the EMA stack + momentum filters are catching weak setups.
                </p>
              </>
            )}
            {/* v8.2 — Per-scanner breakdown (EMA-cross vs BRR). Lets the
                user see WHERE signals are getting filtered out so they
                can tune the right scanner. Both scanners share the
                global reject reasons (trend / momentum / adx / score). */}
            {fs.by_scanner && (
              <div
                data-testid="cloud-bot-filter-stats-by-scanner"
                className="mt-3 pt-2.5 border-t border-vts-line/40"
              >
                <div className="text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono mb-1.5">
                  By scanner
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: "ema_cross", label: "EMA cross", desc: "R/F + Multi" },
                    { key: "brr", label: "BRR (breakout)", desc: "Multi only" },
                  ].map((s) => {
                    const bucket = fs.by_scanner[s.key] || {};
                    const bScanned = Math.max(0, Number(bucket.scanned) || 0);
                    const bFired = Math.max(0, Number(bucket.fired) || 0);
                    const bRejected = Math.max(0, bScanned - bFired);
                    const fireRate = bScanned ? ((bFired / bScanned) * 100).toFixed(0) : "—";
                    const topReason = [
                      { k: "trend", l: "stack/trend", v: Number(bucket.rejected_trend) || 0 },
                      { k: "score", l: "score", v: Number(bucket.rejected_score) || 0 },
                      { k: "adx", l: "chop", v: Number(bucket.rejected_adx) || 0 },
                      { k: "momentum", l: "momentum", v: Number(bucket.rejected_momentum) || 0 },
                      { k: "other", l: "other", v: Number(bucket.rejected_other) || 0 },
                    ].sort((a, b) => b.v - a.v)[0];
                    return (
                      <div
                        key={s.key}
                        data-testid={`cloud-bot-scanner-${s.key}`}
                        className="bg-vts-surface/50 border border-vts-line rounded p-2"
                      >
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-[10px] font-mono font-semibold text-white">
                            {s.label}
                          </span>
                          <span className="text-[8px] uppercase tracking-wider text-vts-silver/40 font-mono">
                            {s.desc}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] font-mono">
                          <span className="text-vts-silver/60">
                            {bScanned} <span className="text-[8px]">scan</span>
                          </span>
                          <span className="text-vts-profit">
                            {bFired} <span className="text-[8px]">fire</span>
                          </span>
                          <span className="text-vts-loss">
                            {bRejected} <span className="text-[8px]">rej</span>
                          </span>
                          <span className="ml-auto text-[9px] text-vts-blue/80">
                            {fireRate}%
                          </span>
                        </div>
                        {topReason && topReason.v > 0 && (
                          <div className="mt-1 text-[9px] text-vts-silver/50 font-mono">
                            top reject: <span className="text-vts-loss">{topReason.l}</span> ({topReason.v})
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* v8.7 — BLOCK REASONS (24h) widget. Tells the operator WHY a
          signal that passed all scan filters never reached Deriv. Reads
          the persistent `block_reasons_24h` counter from /api/bot/status
          (survives the 500-entry log rotation). Sorted by count desc;
          shows top 5 — the rest collapse into "+N more". The widget
          only appears when there's at least one recorded block so an
          empty fresh-restart state doesn't add noise. */}
      {/* v8.7 — BLOCK REASONS widget. OPERATOR-ONLY: technical diagnostic. */}
      {operatorMode && running && status?.block_reasons_24h && Object.keys(status.block_reasons_24h).length > 0 && (() => {
        const REASON_LABELS = {
          "off-session":            "Off-session (market closed)",
          "stpRNG-rf-blocked":      "Step indices not allowed",
          "balance-unavailable":    "Balance unavailable",
          "min-stake-too-large":    "Min stake > 50% balance",
          "max-stake-too-low":      "Max stake too low",
          "min-above-max":          "Min stake > max stake",
          "deriv-proposal-rejected":"Deriv rejected proposal",
          "no-proposal-id":         "No proposal id returned",
          "deriv-buy-rejected":     "Deriv rejected buy",
          "payout-too-low":         "Payout below gate",
        };
        const entries = Object.entries(status.block_reasons_24h)
          .map(([k, v]) => ({ k, v: Number(v) || 0, label: REASON_LABELS[k] || k }))
          .filter((e) => e.v > 0)
          .sort((a, b) => b.v - a.v);
        if (entries.length === 0) return null;
        const total = entries.reduce((s, e) => s + e.v, 0);
        const top = entries.slice(0, 5);
        const rest = entries.slice(5);
        const restSum = rest.reduce((s, e) => s + e.v, 0);
        return (
          <div
            data-testid="block-reasons-widget"
            className="mt-4 bg-vts-bg/60 border border-vts-line rounded-md px-4 py-3"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
                Block reasons · last 24h
              </div>
              <div
                data-testid="block-reasons-total"
                className="text-[9px] font-mono tabular-nums text-vts-silver/60"
              >
                {total.toLocaleString()} blocked
              </div>
            </div>
            <div className="space-y-1.5">
              {top.map((e) => {
                const pct = total > 0 ? (e.v / total) * 100 : 0;
                return (
                  <div
                    key={e.k}
                    data-testid={`block-reason-row-${e.k}`}
                    className="flex items-center gap-2"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
                        <span className="text-vts-silver truncate" title={e.label}>
                          {e.label}
                        </span>
                        <span className="text-vts-silver/70 tabular-nums shrink-0 ml-2">
                          {e.v.toLocaleString()} <span className="text-vts-silver/40">({pct.toFixed(0)}%)</span>
                        </span>
                      </div>
                      <div className="h-1 bg-vts-line/40 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-vts-loss/70"
                          style={{ width: `${Math.max(2, pct).toFixed(1)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
              {rest.length > 0 && (
                <div
                  data-testid="block-reasons-more"
                  className="text-[9px] font-mono text-vts-silver/50 pt-1"
                >
                  +{rest.length} more reason{rest.length === 1 ? "" : "s"} ({restSum.toLocaleString()})
                </div>
              )}
            </div>
            <div className="mt-3 pt-2 border-t border-vts-line/40 text-[9px] font-mono text-vts-silver/40">
              Counter survives bot restarts. Most blocks here are session-gate
              skips during quiet UTC hours — they auto-clear when London/NY open.
            </div>
          </div>
        );
      })()}

      {/* v8.9.4 — Market condition badge. OPERATOR-ONLY: aggregated EMA gap stats are diagnostic. */}
      {operatorMode && selectedAccount && running && (
        <div className="mb-4">
          <MarketConditionBadge accountId={selectedAccount} running={running} />
        </div>
      )}

      {/* v8.8c — Risk profile selector. OPERATOR-ONLY: users get the default balanced profile. */}
      {operatorMode && selectedAccount && <RiskProfileSelector accountId={selectedAccount} running={running} />}

      {/* v8.8g — Cold pairs badge. OPERATOR-ONLY: per-symbol cooldown details. */}
      {operatorMode && running && status?.cooldowns && <ColdPairsBadge cooldowns={status.cooldowns} accountId={selectedAccount} />}

      {/* v8.8b — BRR forced-symbol chip picker. OPERATOR-ONLY. */}
      {operatorMode && selectedAccount && <BrrForcedPicker accountId={selectedAccount} running={running} />}

      {/* v8.9.1 — Trail config dashboard panel. OPERATOR-ONLY. */}
      {operatorMode && <TrailConfigPanel />}

      {/* v8.9.6 — BRR gate trace. OPERATOR-ONLY: diagnostic tool. */}
      {operatorMode && selectedAccount && running && (
        <BrrGateTraceWidget accountId={selectedAccount} running={running} />
      )}

      {/* v8.9.10 — Deriv rejection log. OPERATOR-ONLY. Surfaces the raw
          error message for every fire that Deriv refused at proposal or
          buy stage. Answers "why are fires not becoming trades?". */}
      {operatorMode && selectedAccount && (
        <RecentRejectionsWidget accountId={selectedAccount} />
      )}

      {/* Log — OPERATOR-ONLY (raw technical events, [EMA FILTER], [BRR ...] etc.) */}
      {operatorMode && (
      <div className="mt-4">
        <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono mb-2">
          Activity log (last {log.length})
        </div>
        <div
          data-testid="cloud-bot-log"
          className="bg-vts-bg/60 border border-vts-line rounded-md max-h-64 overflow-y-auto divide-y divide-vts-line/40"
        >
          {log.length === 0 ? (
            <div className="px-3 py-6 text-center text-[11px] text-vts-silver/50">
              No activity yet. Start the bot to begin scanning.
            </div>
          ) : (
            log.map((entry, i) => (
              <div key={`${entry.ts}-${i}`} className="px-3 py-1.5 text-[11px] font-mono flex gap-2">
                <span className="text-vts-silver/40 shrink-0">{fmtTime(entry.ts)}</span>
                <span className={levelColor(entry.level)}>{entry.text}</span>
              </div>
            ))
          )}
        </div>
      </div>
      )}
      {/* Recent Trades — independent of the rolling log so trades don't
          disappear after many "Scanning" entries. */}
      <div className="mt-4">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
            Recent trades ({trades.length})
          </div>
          {trades.length > 0 && (
            <div className="text-[10px] font-mono text-vts-silver/50">
              {trades.filter((t) => t.status === "won").length}W ·{" "}
              {trades.filter((t) => t.status === "lost").length}L ·{" "}
              {trades.filter((t) => t.status === "open").length} open
            </div>
          )}
        </div>
        {/* If there are stale "open" trades while the bot is NOT running,
            we auto-reconcile in the background — show a brief notice. */}
        {!running && trades.some((t) => t.status === "open") && (
          <div className="mb-2 px-3 py-2 bg-vts-blue/10 border border-vts-blue/30 rounded-md text-[11px] text-vts-blue font-mono leading-relaxed">
            Syncing open trades with Deriv… (auto-reconciles every 60s).
          </div>
        )}
        <div
          data-testid="cloud-bot-trades"
          className="bg-vts-bg/60 border border-vts-line rounded-md max-h-64 overflow-y-auto divide-y divide-vts-line/40"
        >
          {trades.length === 0 ? (
            <div className="px-3 py-6 text-center text-[11px] text-vts-silver/50">
              No trades yet. The bot only fires on fresh M1 EMA crossovers.
            </div>
          ) : (
            trades.map((t, i) => {
              const isOpen = t.status === "open";
              const won = t.status === "won";
              const profit = Number(t.profit);
              const dir = (t.contract_type || "").toUpperCase() === "MULTUP" ? "▲" : "▼";
              const dirColor = (t.contract_type || "").toUpperCase() === "MULTUP" ? "text-vts-profit" : "text-vts-loss";
              const stratLabel = STRATEGY_LABELS[t.strategy] || (t.strategy ? t.strategy : "—");
              const stratColor = STRATEGY_COLORS[t.strategy] || "bg-vts-silver/10 text-vts-silver/70 border-vts-silver/20";
              return (
                <div
                  key={`${t.contract_id}-${i}`}
                  className="px-3 py-2 text-[11px] font-mono grid grid-cols-12 items-center gap-2"
                  data-testid={`cloud-bot-trade-row-${t.strategy || "unknown"}`}
                >
                  <span className={`col-span-1 ${dirColor}`}>{dir}</span>
                  <span className="col-span-3 text-white truncate">{t.symbol}</span>
                  <span className="col-span-2 text-vts-silver/70 tabular-nums">
                    ${Number(t.stake || 0).toFixed(2)}
                  </span>
                  <span className="col-span-3">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${stratColor}`}
                      data-testid={`cloud-bot-trade-strategy-badge`}
                    >
                      {stratLabel}
                    </span>
                  </span>
                  <span className="col-span-3 text-right tabular-nums">
                    {isOpen ? (
                      <span className="text-vts-silver/60">open</span>
                    ) : Number.isFinite(profit) ? (
                      <span className={won ? "text-vts-profit" : "text-vts-loss"}>
                        {won ? "+" : ""}${profit.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-vts-silver/60">—</span>
                    )}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </div>
      {/* Per-strategy performance — OPERATOR-ONLY. Exposes raw strategy
          tags (ema_cross/breakout_retest/etc.) and tuning jargon (ADX,
          BOS, RSI gates) that traders don't need to see. */}
      {operatorMode && strategyStats.length >= 1 && (
        <div className="mt-4" data-testid="cloud-bot-strategy-stats">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
              Performance by strategy ({strategyStats.length})
            </div>
            <div className="text-[9px] font-mono text-vts-silver/40">
              sorted by P&amp;L
            </div>
          </div>
          {(strategy === "all" || strategy === "both") && (
            <div className="mb-2 px-2.5 py-1.5 bg-vts-blue/10 border border-vts-blue/30 rounded-md text-[10px] font-mono text-vts-blue leading-relaxed">
              💡 v6.1 strict filters + Breakout&nbsp;&amp;&nbsp;Retest secondary scanner active.
              Primary: EMA-cross with ADX&nbsp;≥&nbsp;25, M15 HTF alignment, BOS confirmation, RSI 55/45 gates.
              Secondary: 20-bar breakout → retest (within&nbsp;0.5×ATR) → bullish/bearish confirmation candle.
              Multiplier auto-closes at +10%&thinsp;/&thinsp;−5% of balance and on opposite signals. 2 losses → 15-min pause, max 3 trades per trend direction.
            </div>
          )}
          <div className="bg-vts-bg/60 border border-vts-line rounded-md overflow-hidden">
            <div className="px-3 py-1.5 grid grid-cols-12 gap-2 border-b border-vts-line/60 text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">
              <span className="col-span-3">Strategy</span>
              <span className="col-span-2 text-center">W/L</span>
              <span className="col-span-2 text-center">Win %</span>
              <span className="col-span-2 text-center">ROI</span>
              <span className="col-span-3 text-right">P&amp;L</span>
            </div>
            {strategyStats.map((s) => {
              const wr = Number(s.win_rate) || 0;
              const roi = Number(s.roi) || 0;
              const pnl = Number(s.pnl) || 0;
              const count = Number(s.count) || 0;
              const wrColor =
                count < 3
                  ? "text-vts-silver/50"
                  : wr >= 60
                  ? "text-vts-profit"
                  : wr >= 45
                  ? "text-vts-silver/80"
                  : "text-vts-loss";
              const pnlColor =
                pnl > 0
                  ? "text-vts-profit"
                  : pnl < 0
                  ? "text-vts-loss"
                  : "text-vts-silver/70";
              const roiColor =
                count < 3
                  ? "text-vts-silver/50"
                  : roi > 0
                  ? "text-vts-profit"
                  : roi < 0
                  ? "text-vts-loss"
                  : "text-vts-silver/70";
              const label = STRATEGY_LABELS[s.strategy] || s.strategy;
              const color =
                STRATEGY_COLORS[s.strategy] ||
                "bg-vts-silver/10 text-vts-silver/70 border-vts-silver/20";
              const isPaused = (status?.disabled_strategies || []).includes(s.strategy);
              return (
                <div
                  key={s.strategy}
                  className={`px-3 py-2 grid grid-cols-12 gap-2 text-[11px] font-mono items-center border-b border-vts-line/30 last:border-0 ${
                    isPaused ? "opacity-60" : ""
                  }`}
                  data-testid={`cloud-bot-strategy-row-${s.strategy}`}
                >
                  <span className="col-span-3 flex items-center gap-1.5 flex-wrap">
                    <span
                      className={`inline-block px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${color}`}
                    >
                      {label}
                    </span>
                    {isPaused && (
                      <>
                        <span
                          className="inline-block px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider border bg-vts-loss/15 text-vts-loss border-vts-loss/40"
                          data-testid={`cloud-bot-strategy-paused-${s.strategy}`}
                        >
                          paused
                        </span>
                        <button
                          type="button"
                          data-testid={`cloud-bot-strategy-resume-${s.strategy}`}
                          onClick={() => handleUnpauseStrategy(s.strategy)}
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider border bg-vts-blue/15 text-vts-blue border-vts-blue/40 hover:bg-vts-blue/30 transition cursor-pointer"
                          title="Manually resume this strategy. Rolling stats reset to fresh window."
                        >
                          ▶ resume
                        </button>
                      </>
                    )}
                    {Number(s.open_count) > 0 && (
                      <span className="text-[9px] text-vts-blue tabular-nums">
                        +{s.open_count} open
                      </span>
                    )}
                  </span>
                  <span className="col-span-2 text-center text-vts-silver/70 tabular-nums">
                    <span className="text-vts-profit">{s.wins}W</span>
                    <span className="text-vts-silver/40 mx-1">·</span>
                    <span className="text-vts-loss">{s.losses}L</span>
                  </span>
                  <span className={`col-span-2 text-center tabular-nums font-semibold ${wrColor}`}>
                    {count > 0 ? `${wr.toFixed(0)}%` : "—"}
                  </span>
                  <span className={`col-span-2 text-center tabular-nums ${roiColor}`}>
                    {count > 0 ? `${roi >= 0 ? "+" : ""}${roi.toFixed(1)}%` : "—"}
                  </span>
                  <span className={`col-span-3 text-right tabular-nums ${pnlColor}`}>
                    {pnl >= 0 ? "+" : ""}${pnl.toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-vts-silver/40 mt-1.5 font-mono">
            ROI = total P&amp;L ÷ total stake. Strategies with &lt; 3 closed trades
            show stats once they get a sample.
          </p>
        </div>
      )}
      {/* Duration Performance — per-duration aggregated WR / P&L over the
          last 30 days of tracked trades. Answers "is 5m or 15m R/F winning
          on MY account?" without leaving the dashboard. Driven by
          /api/track/duration-stats; appears once at least one settled
          tracked trade exists.
          v8.9.78 — 1-tick duration retired (user feedback: -$9k on 522 trades,
          "link gambling"). Bot no longer fires 1T; historical 1T rows hidden. */}
      {durationStats.filter((d) => d.duration !== "1T" && d.duration !== "1t").length > 0 && (
        <div className="mt-4" data-testid="cloud-bot-duration-stats">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
              Duration performance · last 30 days
            </div>
            <div className="text-[9px] font-mono text-vts-silver/40">
              breakeven 52.1% on 95% payout
            </div>
          </div>
          <div className="bg-vts-bg/60 border border-vts-line rounded-md overflow-hidden">
            <div className="px-3 py-1.5 grid grid-cols-12 gap-2 border-b border-vts-line/60 text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">
              <span className="col-span-3">Duration</span>
              <span className="col-span-3 text-center">W/L</span>
              <span className="col-span-3 text-center">Win %</span>
              <span className="col-span-3 text-right">P&amp;L</span>
            </div>
            {durationStats
              .filter((d) => d.duration !== "1T" && d.duration !== "1t")
              .map((d) => {
              const wr = (d.win_rate || 0) * 100;
              const wrColor =
                d.total < 5
                  ? "text-vts-silver/50"
                  : wr >= 55
                  ? "text-vts-profit"
                  : wr >= 52.1
                  ? "text-vts-silver/80"
                  : "text-vts-loss";
              const pnlColor =
                d.profit > 0 ? "text-vts-profit" : d.profit < 0 ? "text-vts-loss" : "text-vts-silver/70";
              return (
                <div
                  key={d.duration}
                  className="px-3 py-1.5 grid grid-cols-12 gap-2 text-[11px] font-mono items-center border-b border-vts-line/30 last:border-0"
                  data-testid={`cloud-bot-duration-row-${d.duration}`}
                >
                  <span className="col-span-3 text-white">
                    <span className="inline-block px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wider border bg-vts-blue/10 text-vts-blue border-vts-blue/30">
                      {d.duration}
                    </span>
                  </span>
                  <span className="col-span-3 text-center text-vts-silver/70 tabular-nums">
                    <span className="text-vts-profit">{d.won}W</span>
                    <span className="text-vts-silver/40 mx-1">·</span>
                    <span className="text-vts-loss">{d.lost}L</span>
                  </span>
                  <span className={`col-span-3 text-center tabular-nums font-semibold ${wrColor}`}>
                    {d.total > 0 ? `${wr.toFixed(0)}%` : "—"}
                  </span>
                  <span className={`col-span-3 text-right tabular-nums ${pnlColor}`}>
                    {d.profit >= 0 ? "+" : ""}${Number(d.profit || 0).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-vts-silver/40 mt-1.5 font-mono leading-snug">
            Green = above 55% WR (clearly profitable). Grey = above breakeven (52.1%).
            Red = below breakeven — consider pausing this duration.
            <span className="block mt-1 text-vts-silver/55">1-tick contracts retired in v8.9.78 — no new 1T trades will fire.</span>
          </p>
        </div>
      )}
      {/* Win-rate by Symbol leaderboard — surfaces which pairs the bot
          actually makes money on so users can refine config over time. */}
      {symbolStats.length > 0 && (
        <div className="mt-4" data-testid="cloud-bot-symbol-stats">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-vts-silver/60 font-mono">
              Win-rate by symbol ({symbolStats.length} pairs)
            </div>
            <div className="text-[9px] font-mono text-vts-silver/40">sorted by P&amp;L</div>
          </div>
          <div className="bg-vts-bg/60 border border-vts-line rounded-md max-h-72 overflow-y-auto">
            <div className="px-3 py-1.5 grid grid-cols-12 gap-2 border-b border-vts-line/60 text-[9px] uppercase tracking-wider text-vts-silver/50 font-mono">
              <span className="col-span-4">Symbol</span>
              <span className="col-span-3 text-center">Trades</span>
              <span className="col-span-2 text-center">Win %</span>
              <span className="col-span-3 text-right">P&amp;L</span>
            </div>
            {symbolStats.map((s) => {
              const wr = Number(s.win_rate) || 0;
              const count = Number(s.count) || 0;
              const blacklisted = autoBlacklist && count >= 10 && wr < 40;
              const wrColor = wr >= 60 ? "text-vts-profit" : wr >= 45 ? "text-vts-silver/80" : "text-vts-loss";
              const pnlColor = Number(s.pnl) > 0 ? "text-vts-profit" : Number(s.pnl) < 0 ? "text-vts-loss" : "text-vts-silver/70";
              return (
                <div
                  key={s.symbol}
                  className={`px-3 py-1.5 grid grid-cols-12 gap-2 text-[11px] font-mono items-center border-b border-vts-line/30 last:border-0 ${
                    blacklisted ? "opacity-60" : ""
                  }`}
                  data-testid={`cloud-bot-symbol-row-${s.symbol}`}
                >
                  <span className="col-span-4 text-white truncate">
                    {s.symbol}
                    {blacklisted && (
                      <span className="ml-1.5 text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-vts-loss/15 text-vts-loss border border-vts-loss/30">
                        skip
                      </span>
                    )}
                  </span>
                  <span className="col-span-3 text-center text-vts-silver/70 tabular-nums">
                    <span className="text-vts-profit">{s.wins}W</span>
                    <span className="text-vts-silver/40 mx-1">·</span>
                    <span className="text-vts-loss">{s.losses}L</span>
                  </span>
                  <span className={`col-span-2 text-center tabular-nums font-semibold ${wrColor}`}>
                    {wr.toFixed(0)}%
                  </span>
                  <span className={`col-span-3 text-right tabular-nums ${pnlColor}`}>
                    {Number(s.pnl) >= 0 ? "+" : ""}${Number(s.pnl).toFixed(2)}
                  </span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-vts-silver/40 mt-1.5 font-mono">
            Tip: pairs with &lt; 50% win-rate over 20+ trades may indicate the strategy doesn't fit that market right now.
          </p>
        </div>
      )}

      {/* MasterSpec §B — Risk Disclaimer modal, shown on every Start. */}
      {disclaimerOpen && (
        <div
          data-testid="risk-disclaimer-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) setDisclaimerOpen(false);
          }}
        >
          <div className="w-full max-w-md bg-vts-card border border-vts-line rounded-lg shadow-2xl">
            <div className="px-5 py-4 border-b border-vts-line flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0" />
              <h3 className="text-base font-mono font-semibold text-white tracking-wide">
                RISK DISCLAIMER
              </h3>
            </div>
            <div className="px-5 py-4 space-y-3 text-[12px] font-mono leading-relaxed text-vts-silver/80">
              <p>
                Trading derivatives on forex, commodities, and crypto carries a <span className="text-amber-300">high level of risk</span>.
                You can lose your entire deposit. Past performance is not a guarantee of future results.
              </p>
              <p>
                This bot is fully automated. It will place real-money trades on the account you have connected
                until you tap Stop, the daily safety limits trigger, or the session loss limit (−10%) is reached.
              </p>
              <ul className="list-disc list-inside text-[11px] text-vts-silver/60 space-y-1 pl-1">
                <li>Trade only with funds you can afford to lose.</li>
                <li>The bot does not guarantee profits.</li>
                <li>Network outages, broker errors, and rate limits may delay sell decisions.</li>
                <li>You are solely responsible for any losses incurred.</li>
              </ul>
              <label className="flex items-start gap-2 cursor-pointer pt-2">
                <input
                  type="checkbox"
                  data-testid="risk-disclaimer-checkbox"
                  checked={disclaimerChecked}
                  onChange={(e) => setDisclaimerChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 accent-vts-blue cursor-pointer"
                />
                <span className="text-[12px] text-vts-silver/90">
                  I accept the risk disclaimer and trade with funds I can afford to lose.
                </span>
              </label>
            </div>
            <div className="px-5 py-3 border-t border-vts-line flex items-center justify-end gap-2 bg-vts-bg/60">
              <button
                type="button"
                data-testid="risk-disclaimer-cancel"
                onClick={() => setDisclaimerOpen(false)}
                className="px-3 py-1.5 text-[12px] font-mono uppercase tracking-wider text-vts-silver/70 hover:text-white transition"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="risk-disclaimer-agree"
                disabled={!disclaimerChecked}
                onClick={handleConfirmStart}
                className="px-4 py-1.5 text-[12px] font-mono uppercase tracking-wider rounded bg-vts-profit hover:bg-vts-profit/90 text-white disabled:bg-vts-silver/20 disabled:text-vts-silver/40 disabled:cursor-not-allowed transition flex items-center gap-1.5"
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Agree &amp; Start
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CloudBotPanel;
